/**
 * WizardDraftDialog - Layer 6 Draft Generation Dialog
 *
 * Zeigt einen generierten Entwurf für eine Optimierungsempfehlung an.
 * Verwendet LLM mit Grounding um kontextspezifische Umsetzungsvorschläge zu erstellen.
 */

import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Paper,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import { optimizationApi } from '../services/api';
import { RecommendationDraft } from '../types/api';
import { renderMarkdownToSafeHtml } from '../utils/markdown';

interface WizardDraftDialogProps {
  open: boolean;
  onClose: () => void;
  projectId: number;
  testId: number;
  criterionKey: string;
  criterionName: string;
  recommendationIndex: number;
  recommendationText: string;
  recommendationReasoning?: string;
}

const WizardDraftDialog: React.FC<WizardDraftDialogProps> = ({
  open,
  onClose,
  projectId,
  testId,
  criterionKey,
  criterionName,
  recommendationIndex,
  recommendationText,
  recommendationReasoning,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<RecommendationDraft | null>(null);
  const requestIdRef = useRef(0);

  // Reset state when dialog opens with new recommendation
  useEffect(() => {
    if (open) {
      setError(null);
      setDraft(null);
      // Increment requestId to invalidate any pending requests
      requestIdRef.current += 1;
      const currentRequestId = requestIdRef.current;
      loadOrGenerateDraft(currentRequestId);
    }
  }, [open, projectId, testId, criterionKey, recommendationIndex]);

  const loadOrGenerateDraft = async (currentRequestId: number) => {
    setLoading(true);
    setError(null);

    try {
      // First check if draft already exists
      const checkResponse = await optimizationApi.checkDraft(
        projectId,
        testId,
        criterionKey,
        recommendationIndex
      );

      // Check if this request is still valid (not superseded by a newer one)
      if (currentRequestId !== requestIdRef.current) return;

      if (checkResponse.data.exists && checkResponse.data.draft) {
        // Draft exists, use it
        setDraft(checkResponse.data.draft);
        setLoading(false);
        return;
      }

      // Draft doesn't exist, generate it
      const generateResponse = await optimizationApi.generateDraft(
        projectId,
        testId,
        criterionKey,
        recommendationIndex,
        recommendationText,
        recommendationReasoning
      );

      // Check if this request is still valid before updating state
      if (currentRequestId !== requestIdRef.current) return;

      setDraft(generateResponse.data);
    } catch (err: any) {
      // Only set error if this request is still valid
      if (currentRequestId !== requestIdRef.current) return;
      console.error('Error loading/generating draft:', err);
      setError(err.message || 'Fehler beim Generieren des Entwurfs');
    } finally {
      // Only update loading if this request is still valid
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <AutoAwesomeIcon />
          <Typography variant="h6" component="span">
            Entwurf für Empfehlung
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ color: 'white' }}
          size="small"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ p: 3 }}>
        {/* Context: Show the recommendation */}
        <Paper
          variant="outlined"
          sx={{
            p: 2,
            mb: 3,
            backgroundColor: 'grey.50',
          }}
        >
          <Typography variant="subtitle2" color="text.secondary" gutterBottom>
            {criterionName} - Empfehlung {recommendationIndex + 1}
          </Typography>
          <Typography variant="body2">
            {recommendationText}
          </Typography>
        </Paper>

        <Alert severity="warning" sx={{ mb: 3 }}>
          <Typography variant="body2" fontWeight={600} gutterBottom>
            Hinweis: KI-generierter Entwurf
          </Typography>
          <Typography variant="body2">
            Dieser Vorschlag wurde automatisiert von einer KI erstellt und kann Fehler
            enthalten. Empfehlungen können rechtliche Implikationen haben (u. a. Datenschutz/DSGVO,
            Wettbewerbs- und Werberecht, Urheberrecht, Kennzeichnungspflichten z. B. bei
            Kundenstimmen oder Testimonials). Bitte vor der Umsetzung inhaltlich und rechtlich prüfen
            (lassen).
          </Typography>
        </Alert>

        <Divider sx={{ mb: 3 }} />

        {/* Loading State */}
        {loading && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              py: 8,
            }}
          >
            <CircularProgress
              size={48}
              sx={{
                color: '#764ba2',
                mb: 2,
              }}
            />
            <Typography variant="body1" color="text.secondary">
              Entwurf wird erstellt...
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Dieser Vorgang kann bis zu 90 Sekunden dauern
            </Typography>
          </Box>
        )}

        {/* Error State */}
        {error && !loading && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {/* Draft Content */}
        {draft && !loading && (
          <Box>
            <Typography
              variant="subtitle2"
              color="text.secondary"
              gutterBottom
              sx={{ mb: 2 }}
            >
              Entwurf
            </Typography>
            <Paper
              variant="outlined"
              sx={{
                p: 3,
                maxHeight: '50vh',
                overflow: 'auto',
              }}
            >
              <Typography
                component="div"
                variant="body2"
                sx={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'inherit',
                  '& strong, & b': { fontWeight: 600 },
                  '& h1, & h2, & h3': {
                    fontWeight: 600,
                    marginTop: '16px',
                    marginBottom: '8px',
                  },
                  '& h1': { fontSize: '1.5rem' },
                  '& h2': { fontSize: '1.25rem' },
                  '& h3': { fontSize: '1.1rem' },
                }}
                dangerouslySetInnerHTML={{
                  __html: renderMarkdownToSafeHtml(draft.draft_content),
                }}
              />
            </Paper>

            {/* Token info */}
            {(draft.tokens_input || draft.tokens_output) && (
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mt: 1, display: 'block' }}
              >
                Tokens: {draft.tokens_input?.toLocaleString() || 0} Input / {draft.tokens_output?.toLocaleString() || 0} Output
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained">
          Schließen
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WizardDraftDialog;
